/**
 * Global Authentication Middleware
 *
 * Protects application routes based on authentication state and module permissions.
 *
 * Behavior:
 * - Login route is accessible regardless of authentication status
 * - Protected routes require authentication (redirects to /auth/login if not authenticated)
 * - Authenticated users accessing /auth/login are redirected to /
 * - On client-side page reload, waits for session restoration before deciding
 * - On server-side, allows navigation to proceed (auth handled on client)
 * - Module-protected routes check user's module permissions (redirects to / if not authorized)
 * - Coordinates with visibility check and interceptor to avoid duplicate error handling
 */
import { canAccessRoute } from '~/composables/usePermissions'
import { isAuthFailureInProgress } from '~/utils/auth.session-handler'

export default defineNuxtRouteMiddleware(async to => {
  // Define the login route
  const loginRoute = '/auth/login'

  // On server-side, we don't have access to authentication state (cookies are HTTP-only)
  // Allow all navigation on server - the client will handle auth redirects after hydration
  // This prevents the "flash" of login page during SSR when a valid session exists
  if (import.meta.server) {
    return
  }

  // --- Client-side only from here ---

  // Get the Nuxt app instance to access Pinia properly
  const nuxtApp = useNuxtApp()

  // Get authentication state from the auth store
  // Use $pinia to ensure proper Pinia context
  const authStore = useAuthStore(nuxtApp.$pinia)

  // Wait for session restoration to complete before making routing decisions
  // This prevents the "flash" of login page when a valid session exists
  if (!authStore.sessionRestored) {
    // Session restoration hasn't completed yet - wait for it
    // The auth.init plugin runs with enforce: 'pre', so it should complete soon
    await authStore.restoreSession()
  }

  // Proactive session validation: Check if the token is expired after restoration
  // This catches cases where a user returns after long inactivity (e.g., tab left open for days)
  // Skip if auth failure is already being handled (e.g., by visibility check plugin)
  if (authStore.isAuthenticated && authStore.isTokenExpired && !isAuthFailureInProgress()) {
    try {
      // Token is expired - attempt to refresh it before navigation
      await authStore.refresh()
    } catch (error) {
      // Refresh failed - session has truly expired
      // Clear state and redirect to login
      // Note: Toast message is handled by the interceptor/visibility-check to avoid duplicates
      console.error('Session expired during navigation - redirecting to login')
      authStore.clearState()
      return navigateTo(loginRoute)
    }
  }

  const isAuthenticated = authStore.isAuthenticated

  // Get the current route path
  const currentPath = to.path

  // Check if the current route is the login route
  const isLoginRoute = currentPath === loginRoute

  // Allow navigation to login route regardless of authentication status
  if (isLoginRoute) {
    // Special case: If authenticated user tries to reach login, redirect to home
    if (isAuthenticated) {
      return navigateTo('/')
    }
    // Allow access to login route
    return
  }

  // For all other routes (protected routes):
  // If not authenticated, redirect to login
  if (!isAuthenticated) {
    return navigateTo(loginRoute)
  }

  // --- Module-based route protection ---
  // Check if the authenticated user has permission to access this route
  // based on their assigned modules
  if (!canAccessRoute(currentPath)) {
    // User doesn't have access to this module - redirect to home
    // Could also show a toast notification here
    console.warn(`Access denied to route: ${currentPath}`)
    return navigateTo('/')
  }

  // If authenticated and has module access, allow navigation
  return
})
